import { computed, inject, Injectable } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '@price-management-console/auth';
import { APP_ENVIRONMENT } from '@price-management-console/shared-ui';
import { catchError, map, of, switchMap } from 'rxjs';
import { PermissionApiService } from './permission-api.service';
import { AdGroupPermissionsResponse } from './permission-api.types';
import {
  PermissionAction,
  PermissionActionType,
  PermissionFeature,
  PermissionFeatureType,
  PermissionMap,
} from './permission.types';

const BACKEND_FEATURE_MAP: Record<string, PermissionFeatureType> = {
  // v0 feature keys
  'routing-protocol': PermissionFeature.RoutingProtocol,
  'rate-management': PermissionFeature.RateManagement,
  // v1 feature keys
  routing_protocol: PermissionFeature.RoutingProtocol,
  rate_management: PermissionFeature.RateManagement,
  ROUTING_PROTOCOL: PermissionFeature.RoutingProtocol,
  RATE_MANAGEMENT: PermissionFeature.RateManagement,
};

const BACKEND_ACTION_MAP: Record<string, PermissionActionType> = {
  CREATE: PermissionAction.Create,
  SEARCH: PermissionAction.Search,
  VIEW: PermissionAction.View,
  EDIT: PermissionAction.Edit,
  CLONE: PermissionAction.Clone,
  EXPORT: PermissionAction.Export,
  IMPORT: PermissionAction.Import,
  DELETE: PermissionAction.Delete,
  RENEW: PermissionAction.Renew,
  PUBLISH: PermissionAction.Publish,
};

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private readonly authService = inject(AuthService);
  private readonly permissionApiService = inject(PermissionApiService);
  private readonly environment = inject(APP_ENVIRONMENT);

  // Declarative RxJS pipeline feeding the read-only signal directly
  readonly permissions = toSignal(
    toObservable(this.authService.tokenResult).pipe(
      switchMap((tokenResult) => {
        if (!tokenResult) {
          return of(null);
        }

        const version = this.environment.SECURITY_VERSION ?? 'v0';

        // If 'v0' security, just map the custom claims from the JWT
        if (version === 'v0') {
          return of(this.mapV0Claims(tokenResult.claims));
        }

        // At this point, this is 'v1' security - using permission API call
        const userId = this.authService.userId();

        if (!userId) {
          console.error(
            'Could not determine username from claims. User is unauthorized.'
          );
          return of({} as PermissionMap);
        }

        return this.permissionApiService.getPermissions(userId).pipe(
          map((response) => this.mapPermissions(response)),
          catchError((err) => {
            console.error('Failed to fetch permissions', err);
            return of({} as PermissionMap);
          })
        );
      })
    ),
    { initialValue: null }
  );

  // Unified loading state
  readonly isLoading = computed(() => {
    const user = this.authService.user();
    // Loading is true if auth is loading, OR if user is authenticated but permissions are not yet loaded
    return user === undefined || (user !== null && this.permissions() === null);
  });

  hasPermission(
    feature: PermissionFeatureType,
    action: PermissionActionType
  ): boolean {
    return this.permissions()?.[feature]?.includes(action) ?? false;
  }

  hasFeatureAccess(feature: PermissionFeatureType): boolean {
    return !!this.permissions()?.[feature];
  }

  hasAnyPermission(
    checks: [PermissionFeatureType, PermissionActionType][]
  ): boolean {
    return checks.some(([feature, action]) =>
      this.hasPermission(feature, action)
    );
  }

  hasAllPermissions(
    checks: [PermissionFeatureType, PermissionActionType][]
  ): boolean {
    return checks.every(([feature, action]) =>
      this.hasPermission(feature, action)
    );
  }

  private mapPermissions(response: AdGroupPermissionsResponse): PermissionMap {
    const map: PermissionMap = {};

    for (const permissionStr of response?.effectivePermissions ?? []) {
      const [backendFeature, backendAction] = permissionStr.split('.');
      if (backendFeature && backendAction) {
        const feature = BACKEND_FEATURE_MAP[backendFeature];
        const action = BACKEND_ACTION_MAP[backendAction.toUpperCase()];

        if (feature && action) {
          const actions = (map[feature] ??= []);
          if (!actions.includes(action)) {
            actions.push(action);
          }
        }
      }
    }
    //TODO: Remove this mock once the API returns the rate management feature
    map[PermissionFeature.RateManagement] ??= [
      PermissionAction.View,
      PermissionAction.Create,
      PermissionAction.Edit,
      PermissionAction.Delete,
      PermissionAction.Clone,
      PermissionAction.Import,
      PermissionAction.Export,
      PermissionAction.Publish,
      PermissionAction.Renew,
      PermissionAction.Search,
    ];
    return map;
  }

  private mapV0Claims(
    claims: Record<string, unknown> | null | undefined
  ): PermissionMap {
    const claimsPermissions = claims?.['permissions'];
    if (!claimsPermissions || typeof claimsPermissions !== 'object') {
      return {};
    }

    const map: PermissionMap = {};

    for (const [featureKey, perms] of Object.entries(
      claimsPermissions as Record<string, string[]>
    )) {
      const feature = BACKEND_FEATURE_MAP[featureKey];

      if (feature && Array.isArray(perms)) {
        const actionsList = (map[feature] ??= []);
        for (const p of perms) {
          const normalizedAction = String(p).toUpperCase();
          const action = BACKEND_ACTION_MAP[normalizedAction];
          if (action && !actionsList.includes(action)) {
            actionsList.push(action);
          }
        }
      }
    }

    return map;
  }
}
